/* =====================================================================
   MD Solutions · Lebenszyklus-Landingpage · mdx-landing.js
   - kein Framework, keine Library, kein Build-Schritt
   - ein passiver Scroll-Listener + requestAnimationFrame
   - pro Frame erst alle Maße lesen, dann schreiben (kein Layout-Thrashing)
   - Animationen laufen in CSS (transform/opacity); JS setzt nur --p (0…1)
   - Scroll-Szenen nur ab 990 × 600 px und ohne „Bewegung reduzieren“
   - Videos erst im Sichtbereich, nie bei „Bewegung reduzieren“ oder Datensparmodus
   - Theme-Editor: reagiert auf shopify:section:load / unload / block:select
   ===================================================================== */
(function () {
  'use strict';

  /* Mehrere Sections binden das Skript ein: nur einmal initialisieren. */
  if (window.MdxLanding) {
    window.MdxLanding.refresh();
    return;
  }

  var docEl = document.documentElement;
  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mqDesktop = window.matchMedia('(min-width: 990px) and (min-height: 600px)');
  var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection || {};
  var saveData = connection.saveData === true;
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  var scenes = [];
  var animated = false;
  var queued = false;
  var revealObserver = null;
  var videoObserver = null;

  docEl.classList.add('mdx-js');

  function toArray(list) { return Array.prototype.slice.call(list); }
  function all(selector, context) { return toArray((context || document).querySelectorAll(selector)); }
  function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

  /* ------------------------------------------------------------------
     Lebenszyklus-Ring: aktiver Schritt aus Scroll-Fortschritt oder Hover
     ------------------------------------------------------------------ */
  function Ring(el) {
    var self = this;
    this.el = el;
    this.idx = -1;
    this.hover = null;
    this.p = 0;
    this.nodes = all('.mdx-node', el);
    this.steps = all('.mdx-step', el);
    this.centers = all('.mdx-ring__center > span', el);
    this.segs = all('.mdx-ring__seg', el);

    this.nodes.forEach(function (node, i) {
      function enter() { self.hover = i; self.render(); }
      function leave() { self.hover = null; self.render(); }
      node.addEventListener('mouseenter', enter);
      node.addEventListener('focus', enter);
      node.addEventListener('click', enter);
      node.addEventListener('mouseleave', leave);
      node.addEventListener('blur', leave);
    });
  }

  Ring.prototype.render = function () {
    var n = this.steps.length;
    if (!n) return;
    var fromScroll = Math.min(n - 1, Math.floor(this.p * n * 0.999));
    var idx = this.hover !== null ? this.hover : fromScroll;
    if (idx === this.idx) return;
    this.idx = idx;
    for (var i = 0; i < n; i++) {
      var on = i === idx;
      this.steps[i].classList.toggle('is-active', on);
      if (this.centers[i]) this.centers[i].classList.toggle('is-active', on);
      if (this.nodes[i]) {
        this.nodes[i].classList.toggle('is-active', on);
        this.nodes[i].classList.toggle('is-done', i < idx);
        this.nodes[i].setAttribute('aria-pressed', String(on));
      }
      if (this.segs[i]) this.segs[i].classList.toggle('is-done', i < idx);
    }
  };

  /* ------------------------------------------------------------------
     Elemente einsammeln (beim Start und nach Änderungen im Theme-Editor)
     ------------------------------------------------------------------ */
  function collectScenes() {
    scenes = all('[data-mdx-scene]').map(function (el) {
      if (el.hasAttribute('data-mdx-ring') && !el.__mdxRing) el.__mdxRing = new Ring(el);
      return el;
    });
  }

  function collectReveals() {
    var items = all('.mdx-reveal:not([data-mdx-seen])');
    items.forEach(function (el) { el.setAttribute('data-mdx-seen', ''); });
    if (designMode || mqReduce.matches || !('IntersectionObserver' in window)) return;

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.remove('is-pending');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -12% 0px', threshold: 0.12 });
    }

    var vh = window.innerHeight;
    items.forEach(function (el) {
      if (el.getBoundingClientRect().top > vh * 0.9) {
        el.classList.add('is-pending');
        revealObserver.observe(el);
      }
    });
  }

  function collectVideos() {
    var videos = all('[data-mdx-video] video:not([data-mdx-seen])');
    videos.forEach(function (v) { v.setAttribute('data-mdx-seen', ''); });
    /* Bei reduzierter Bewegung oder Datensparmodus bleibt das Posterbild stehen. */
    if (mqReduce.matches || saveData || !('IntersectionObserver' in window)) return;

    if (!videoObserver) {
      videoObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            var attempt = video.play();
            if (attempt && attempt.catch) attempt.catch(function () {});
          } else if (!video.paused) {
            video.pause();
          }
        });
      }, { rootMargin: '120px 0px', threshold: 0.2 });
    }

    videos.forEach(function (video) {
      video.muted = true;
      video.addEventListener('playing', function () { video.classList.add('is-playing'); });
      videoObserver.observe(video);
    });
  }

  /* ------------------------------------------------------------------
     Frame: Scroll-Fortschritt je Szene
     ------------------------------------------------------------------ */
  function queue() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(frame);
  }

  function frame() {
    queued = false;
    if (!animated) return;
    var vh = window.innerHeight;

    // 1) lesen
    var rects = scenes.map(function (el) { return el.getBoundingClientRect(); });

    // 2) schreiben
    scenes.forEach(function (el, i) {
      var rect = rects[i];
      var total = rect.height - vh;
      var p = total > 0 ? clamp01(-rect.top / total) : 0;
      el.style.setProperty('--p', p.toFixed(4));
      if (el.__mdxRing) {
        el.__mdxRing.p = p;
        el.__mdxRing.render();
      }
    });
  }

  function setMode() {
    animated = !mqReduce.matches && mqDesktop.matches;
    docEl.classList.toggle('mdx-anim', animated);
    scenes.forEach(function (el) {
      if (!animated) el.style.removeProperty('--p');
      if (el.__mdxRing) {
        el.__mdxRing.p = 0;
        el.__mdxRing.idx = -1;
        el.__mdxRing.render();
      }
    });
    queue();
  }

  function refresh() {
    collectScenes();
    collectReveals();
    collectVideos();
    setMode();
  }

  window.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', queue, { passive: true });
  [mqReduce, mqDesktop].forEach(function (mq) {
    if (mq.addEventListener) mq.addEventListener('change', setMode);
    else if (mq.addListener) mq.addListener(setMode);
  });

  /* Theme-Editor */
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', function () { window.setTimeout(refresh, 0); });
  document.addEventListener('shopify:section:reorder', queue);
  document.addEventListener('shopify:block:select', function (event) {
    var step = event.target && event.target.closest ? event.target.closest('.mdx-step, .mdx-ring__nodes li') : null;
    var scene = step ? step.closest('[data-mdx-ring]') : null;
    if (!scene || !scene.__mdxRing) return;
    var ring = scene.__mdxRing;
    var index = parseInt(step.getAttribute('data-index'), 10);
    if (!isNaN(index)) { ring.hover = index; ring.render(); }
  });
  document.addEventListener('shopify:block:deselect', function (event) {
    var scene = event.target && event.target.closest ? event.target.closest('[data-mdx-ring]') : null;
    if (scene && scene.__mdxRing) { scene.__mdxRing.hover = null; scene.__mdxRing.render(); }
  });

  window.MdxLanding = { refresh: refresh };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh);
  else refresh();
})();
